import { observer } from 'mobx-react-lite';

import { useParams } from 'react-router';
import { head, isEmpty } from 'lodash';
import styles from './index.module.scss';
import moment from 'moment';
import { eventNameOptions, eventType } from '../events-fields-options';
import classNames from 'classnames';
import EventContent from './EventContent';
import EventVersion from './EventVersion';
import EventMeta from '../EventMeta';
import AmendClinicalEvent from '../Actions/AmendClinicalEvent';
import VoidClinicalEvent from '../Actions/VoidClinicalEvent';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import useRtmEventsService from '../service';
import { formatTime } from '../../../../services/timeService';
import { dateTimeFormat } from '../../../../services/timeService/types';

const EventDetails = ({ uuid }: { uuid: string }) => {
  const { patientUuid = '' } = useParams<{ patientUuid: string }>();
  const { allowAmmend, allowVoid } = useRtmEventsService(patientUuid);

  const { getEvent } = useRtmEventsService(patientUuid);
  const event = getEvent(uuid);

  const currentVersion = head(event?.versions);
  const versions = event?.versions?.slice(1);

  const eventName = eventNameOptions?.find(
    (option) => option.value === currentVersion?.eventName,
  )?.label;

  const editableCount = moment.duration(
    moment(
      moment.tz(event?.serviceTime, event?.localeTimezone ?? moment.tz.guess()).add(30, 'days'),
    ).diff(moment()),
  );

  return event ? (
    <div className={styles.rightSection}>
      <div className={classNames('flex justify-between leading-none', styles.header)}>
        <div className="flex flex-col">
          <div
            className={classNames(
              'font-semibold text-sm text-neutrals-n-300 mb-2',
              styles.eventName,
            )}
          >
            {eventName}
          </div>
          {currentVersion?.timestamp ? (
            <div className={classNames('text-neutrals-n-300 text-xs', styles.createdAt)}>
              Created at ·{' '}
              {formatTime(
                currentVersion?.timestamp,
                dateTimeFormat.DATE_TIME_FORMAT,
                event.localeTimezone,
              )}
            </div>
          ) : null}
          <EventMeta uuid={event.uuid} />
          {allowAmmend(event.uuid) && allowVoid(event.uuid) ? (
            <div
              className={classNames(
                'text-neutrals-n-300 text-xs flex items-center',
                styles.daysLeft,
              )}
            >
              <LockOpenOutlinedIcon className={'w-svg-ms h-auto mr-0.5'} />
              {editableCount.asDays()?.toFixed(0)} days until this event becomes uneditable.
            </div>
          ) : null}
        </div>
        <div className={styles.actions}>
          <VoidClinicalEvent
            uuid={uuid}
            isMonitoring={currentVersion?.eventName === eventType.monitoringAndAssessment}
          />
          <AmendClinicalEvent uuid={uuid} />
        </div>
      </div>
      <div className={classNames('mt-4 pt-4', styles.eventContentWrapper)}>
        <EventContent
          version={currentVersion}
          eventTime={event.serviceTime}
          timezone={event.localeTimezone}
          showWarning={allowAmmend(event.uuid) && allowVoid(event.uuid)}
        />
        {!isEmpty(versions) ? (
          <>
            <div className={'text-sm font-semibold mb-2.5'}>Amend versions</div>
            {versions?.map((version) => (
              <EventVersion
                key={version.uuid}
                version={version}
                eventTime={event.serviceTime}
                timezone={event.localeTimezone}
              />
            ))}
          </>
        ) : null}
      </div>
    </div>
  ) : null;
};

export default observer(EventDetails);
